import {
  StyledContainerColumnLeft,
  StyledContainerColumnRight,
  StyledContainerRowBetween,
} from '../../../styles/ContainerStyles'
import ScheduleDialogDescriptionBlock from '../../Schedule/ScheduleDialogDescriptionBlock'
import { Stack, Tooltip, Typography } from '@mui/material'
import TimePickersRow from './TimePickersRow'
import { checkIfAnyTimePeriodOverlaps, checkIfTimePeriodOverlaps, getIntervalSumString } from '../../../store/utils'
import { SquareButton } from '../../../styles/ButtonStyles'
import { Add } from '@mui/icons-material'
import React, { Dispatch, SetStateAction } from 'react'
import { t } from 'i18next'
import { HoursInterval } from '../../../store/types'
import dayjs from 'dayjs'
import DateFormat from '../../../store/types/enums/DateFormat'
import { NotificationState } from '../../../store/redux/reducers/notification'
import { getFlexDirection, getGap } from '../../../styles/stylePropsFunctions'

type TimeIntervalsProps = {
  isSmallScreen: boolean
  timePeriods: HoursInterval[]
  setTimePeriods: Dispatch<SetStateAction<HoursInterval[]>>
  displayNotification: (notification: NotificationState) => void
  allValid: boolean
  errorCounter: number
  sum: { hours: number; minutes: number }
}

const TimeIntervalsSection = ({
  isSmallScreen,
  timePeriods,
  setTimePeriods,
  displayNotification,
  allValid,
  errorCounter,
  sum,
}: TimeIntervalsProps) => {
  const handleTimePeriodRemove = (timePeriodId: number) => {
    setTimePeriods(timePeriods.filter(timePeriod => +timePeriod.id !== timePeriodId))
  }

  const handleStartTimeChange = (rowId: number, time: string) => {
    const updatedRows = timePeriods.map(row => {
      if (+row.id === rowId) {
        return { ...row, from: time }
      }

      return row
    })

    setTimePeriods(updatedRows)
  }

  const handleEndTimeChange = (rowId: number, time: string) => {
    const updatedRows = timePeriods.map(row => {
      if (+row.id === rowId) {
        return { ...row, to: time }
      }

      return row
    })

    setTimePeriods(updatedRows)
  }

  const addTimePeriod = () => {
    const lastRow = timePeriods[timePeriods.length - 1]

    if (!dayjs(lastRow.from, DateFormat.HourMinute).isValid() || !dayjs(lastRow.to, DateFormat.HourMinute).isValid()) {
      displayNotification({ message: t('page.schedule.fillCurrentTimeInterval'), type: 'error' })
      return
    }

    if (lastRow?.to) {
      const [hours, minutes] = lastRow.to.split(':').map(Number)

      const midnight = dayjs('00:00', 'HH:mm').add(1, 'day')
      let toDate = dayjs()
        .set('hour', hours)
        .set('minute', minutes + 30)
      if (toDate.isAfter(midnight) || toDate.add(1, 'day').isAfter(midnight)) {
        toDate = midnight
      }

      const newRow: HoursInterval = {
        id: Date.now().toString(),
        from: dayjs().set('hour', hours).set('minute', minutes).format(DateFormat.HourMinute),
        to: toDate.format(DateFormat.HourMinute),
      }

      setTimePeriods([...timePeriods, newRow])
    }
  }

  const getTimePeriodMessage = () => {
    if (checkIfAnyTimePeriodOverlaps(timePeriods))
      return (
        <Typography variant="caption" color="error.main">
          {t('page.schedule.fillOutManyPopup.overlappingTimePeriodsError')}
        </Typography>
      )
    else if (allValid)
      return (
        <Typography variant="caption" color="text.base">
          {getIntervalSumString(sum)}
        </Typography>
      )
    else
      return (
        <Typography variant="caption" color="error.main">
          {t('page.schedule.fillOutManyPopup.hoursError', { count: errorCounter })}
        </Typography>
      )
  }

  return (
    <StyledContainerRowBetween
      sx={{
        gap: getGap(isSmallScreen),
        alignItems: 'flex-start',
        flexDirection: getFlexDirection(isSmallScreen),
      }}
    >
      <StyledContainerColumnLeft>
        <ScheduleDialogDescriptionBlock
          title={t('page.schedule.fillOutManyPopup.timeIntervalsTitle')}
          description={t('page.schedule.fillOutManyPopup.timeIntervalsDescription')}
        />
      </StyledContainerColumnLeft>
      <StyledContainerColumnRight>
        <Stack gap={2}>
          {timePeriods.map((p, i) => (
            <TimePickersRow
              key={p.id}
              index={i}
              timePeriod={p}
              removePeriod={handleTimePeriodRemove}
              changeStartTime={handleStartTimeChange}
              changeEndTime={handleEndTimeChange}
              isOverlapping={checkIfTimePeriodOverlaps(p, timePeriods)}
              length={timePeriods.length}
            />
          ))}

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {getTimePeriodMessage()}

            <Tooltip title={t('page.schedule.fillOutManyPopup.addTimePeriod')}>
              <span>
                <SquareButton
                  aria-label={t('page.schedule.fillOutManyPopup.addTimePeriod')}
                  color="primary"
                  size="small"
                  onClick={e => {
                    e.stopPropagation()
                    addTimePeriod()
                  }}
                >
                  <Add />
                </SquareButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </StyledContainerColumnRight>
    </StyledContainerRowBetween>
  )
}

export default TimeIntervalsSection
